import { useEffect, useMemo, useRef, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ToggleButton } from 'primereact/togglebutton';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/app/hooks';
import { useLazySearchSemenLookupQuery } from '@/app/services/lookups';
import { addToast } from '@/app/slices/toast';
import {
  applyConventionalSexLabel,
  columnDefs,
  columnDefsV2,
} from '@/components/common/advanced-semen-lookup/column-defs';
import { EditSemenDialog } from '@/components/common/edit-semen-dialog';
import { NewSemenDialog } from '@/components/common/new-semen-dialog';
import { SemenFormValues } from '@/components/common/new-semen-form/schema';
import { WithLocalStorage } from '@/components/hoc';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { CSS_CERTIFIED, EXPORT_ENUMS, SEX } from '@/constants';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, lookupSideBar, sideBar } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Semen } from '@/rtk-query.gql';
import { useAddSemen } from '@/services/semen-service';

import { useAdvancedSemenDataSource } from './useAdvancedSemenDataSource';
import { useAdvancedSemenDataSourceV2 } from './useAdvancedSemenDataSourceV2';

import styles from './index.module.css';

type IAdvancedSemenLookupProps = {
  className?: string;
  value?: Semen;
  ownerId?: string;
  fertSiteId?: string;
  exportId?: number;
  label?: string;
  onChange?: (payload: Semen, revision?: string) => void;
  tabIndex?: number;
  disabled?: boolean;
  showAddButton?: boolean;
  showEditButton?: boolean;
};

const DATA_TABLE_ROWS = 50;
const CACHE_BLOCK_SIZE = 500;
const LOAD_DEBOUNCE_MS = 500;
const LOAD_DEBOUNCE_MS_V2 = 50;

const NON_TEXT_SEARCH_FIELDS = new Set([
  'totalEmbryoDevelopmentRate',
  'qualityEmbryoDevelopmentRate',
  'dateReceived',
  'shipment.needByDate',
]);

const NON_TEXT_SEARCH_FIELDS_V2 = new Set([
  'totalEmbryoDevelopmentRate',
  'qualityEmbryoDevelopmentRate',
  'dateReceived',
]);

export const AdvancedSemenLookup = ({
  className,
  value,
  ownerId,
  fertSiteId,
  exportId,
  label,
  onChange,
  tabIndex,
  disabled = false,
  showAddButton = true,
  showEditButton = true,
}: IAdvancedSemenLookupProps) => {
  const { t } = useTranslation();
  const defaultGridOptions = useDefaultGridOptions();
  const mode = useAppSelector((state) => state.preferences.mode);

  const isAdvancedSemenDataSourceV2Enabled = useFeatureFlag(
    'Advanced_Semen_Lookup_V2',
  );
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );

  const semenColumnDefs = useMemo(
    () =>
      applyConventionalSexLabel(
        isAdvancedSemenDataSourceV2Enabled ? columnDefsV2 : columnDefs,
        isSemenConventionalTypeEnabled,
      ),
    [isAdvancedSemenDataSourceV2Enabled, isSemenConventionalTypeEnabled],
  );

  const ALL_OPTION = useMemo(
    () => ({ label: t('common.all'), value: '' }),
    [t],
  );

  const SEARCH_BY_OPTIONS = useMemo(
    () =>
      semenColumnDefs
        .filter(
          ({ field }) =>
            !(
              isAdvancedSemenDataSourceV2Enabled
                ? NON_TEXT_SEARCH_FIELDS_V2
                : NON_TEXT_SEARCH_FIELDS
            ).has(field ?? ''),
        )
        .map(({ headerName, field }) => ({
          label: headerName,
          value: field,
        })),
    [isAdvancedSemenDataSourceV2Enabled, semenColumnDefs],
  );

  const SEARCH_BY_OPTIONS_WITH_ALL = useMemo(
    () => [ALL_OPTION, ...SEARCH_BY_OPTIONS],
    [ALL_OPTION, SEARCH_BY_OPTIONS],
  );

  const ENUM_SEARCH_OPTIONS: Record<
    string,
    { label: string; value: string }[]
  > = useMemo(
    () => ({
      sex: Object.keys(SEX).map((k) => ({
        label:
          isSemenConventionalTypeEnabled && k === 'Unknown'
            ? 'Conventional'
            : k,
        value: k,
      })),
      cssCertified: Object.keys(CSS_CERTIFIED).map((k) => ({
        label: k,
        value: k,
      })),
      export: Object.keys(EXPORT_ENUMS).map((k) => ({ label: k, value: k })),
      sexed: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
    }),
    [isSemenConventionalTypeEnabled],
  );

  const gridRef = useRef<AgGridReact>(null);
  const dialogRef = useRef(null);

  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [searchTermTemp, setSearchTermTemp] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedSearchBy, setSelectedSearchBy] = useState(ALL_OPTION.value);
  const [selectedSemen, setSelectedSemen] = useState<Semen | null>(
    value ?? null,
  );
  const [searchAllSemen, setSearchAllSemen] = useState(false);
  const [inputValue, setInputValue] = useState<string>(value?.name || '');
  const [filterByRanch, setFilterByRanch] = useState(!!ownerId);
  const [filterBySite, setFilterBySite] = useState(!!fertSiteId);
  const [filterByExport, setFilterByExport] = useState(!!exportId);
  const [filterActiveSemen, setFilterActiveSemen] = useState(false);
  const [newSemenDialogVisible, setNewSemenDialogVisible] = useState(false);
  const [editSemenDialogVisible, setEditSemenDialogVisible] = useState(false);
  const [selectedSemenForEdit, setSelectedSemenForEdit] = useState<
    Semen | undefined
  >();
  const [showShipmentConfirmDialog, setShowShipmentConfirmDialog] =
    useState(false);
  const [pendingSemenSelection, setPendingSemenSelection] =
    useState<Semen | null>(null);
  const searchDropdownOptions = ENUM_SEARCH_OPTIONS[selectedSearchBy] ?? null;

  const dispatch = useDispatch();
  const [searchSemenQuery] = useLazySearchSemenLookupQuery();

  const { mutate: addSemen } = useAddSemen();
  const isSearchResetEnabled = useFeatureFlag('DS_SemenSireDonor_Search_Reset');
  const isSpecificViewContextEnabled = useFeatureFlag('Specific_View_Context');

  const { dataSource: dataSourceV1 } = useAdvancedSemenDataSource({
    query: searchSemenQuery,
    searchBy: selectedSearchBy,
    searchTerm,
    ownerId: filterByRanch ? ownerId : undefined,
    fertSiteId: filterBySite ? fertSiteId : undefined,
    exportId: filterByExport ? exportId : undefined,
    filterActiveSemen,
    filterByRanch,
    filterBySite,
    filterByExport,
    skip: isAdvancedSemenDataSourceV2Enabled,
  });

  const { dataSource: dataSourceV2 } = useAdvancedSemenDataSourceV2({
    searchBy: selectedSearchBy,
    searchTerm,
    ownerId: filterByRanch ? ownerId : undefined,
    fertSiteId: filterBySite ? fertSiteId : undefined,
    exportId: filterByExport ? exportId : undefined,
    filterActiveSemen,
    filterByRanch,
    filterBySite,
    filterByExport,
    skip: !isAdvancedSemenDataSourceV2Enabled,
  });

  const dataSource = isAdvancedSemenDataSourceV2Enabled
    ? dataSourceV2
    : dataSourceV1;

  const memoizedDefaultColDef = useMemo(
    () => ({
      ...defaultColDef,
      filter: true,
      filterParams: {
        buttons: ['apply', 'clear'],
        closeOnApply: true,
      },
    }),
    [],
  );

  useEffect(() => {
    setSelectedSemenForEdit(selectedSemen ?? undefined);
  }, [selectedSemen?.id]);

  useEffect(() => {
    setInputValue(value?.name || '');
  }, [value]);

  const handleCloseDialog = () => {
    if (isSearchResetEnabled) {
      setSelectedSearchBy(ALL_OPTION.value);
      setSearchTermTemp('');
      setSearchTerm('');
      setFilterActiveSemen(false);
      setFilterByRanch(false);
      setFilterBySite(false);
      setFilterByExport(false);
      gridRef.current?.api?.setFilterModel(null);
    }
    setDialogVisible(false);
  };

  const dialogFooterTemplate = (disabled: boolean) => {
    return (
      <div className={`mt-3 flex w-full justify-between`}>
        <div>
          <Button
            icon="pi pi-plus"
            label={t('common.new')}
            onClick={() => setNewSemenDialogVisible(true)}
          />
          <Button
            icon="pi pi-pencil"
            label={t('common.edit')}
            text
            disabled={selectedSemen === null}
            onClick={() => setEditSemenDialogVisible(true)}
          />
        </div>
        <div>
          <Button
            label={t('common.cancel')}
            icon="pi pi-times"
            severity="secondary"
            onClick={handleCloseDialog}
            aria-label="Cancel lookup"
          />
          <Button
            label={t('common.select')}
            icon="pi pi-check"
            onClick={handleSelectSemen}
            disabled={disabled}
          />
        </div>
      </div>
    );
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement> | undefined) => {
    if (e) {
      e.preventDefault();
    }

    setSearchTerm(searchTermTemp);
  };

  const handleDialogResize = () => {
    if (gridRef.current) {
      gridRef.current.api.sizeColumnsToFit();
    }
  };

  const handleSelectSemen = () => {
    if (selectedSemen) {
      const hasShipment =
        selectedSemen.shipment?.shipName !== null &&
        selectedSemen.shipment?.shipName !== undefined;

      if (hasShipment) {
        setPendingSemenSelection(selectedSemen);
        setShowShipmentConfirmDialog(true);
        return;
      }

      proceedWithSelection(selectedSemen);
    }
  };

  const proceedWithSelection = (semen: Semen) => {
    setInputValue(semen.name || '');
    onChange?.(semen);
    handleCloseDialog();
    setPendingSemenSelection(null);
  };

  const handleSelectionChanged = (e) => {
    const selectedNode = e.api.getSelectedNodes()[0];

    if (selectedNode && selectedNode.data) {
      setSelectedSemen(selectedNode.data);
    } else {
      setSelectedSemen(null);
    }
  };

  const handleOpenDialog = () => {
    setSearchTermTemp(inputValue);
    setSearchTerm(inputValue);
    // Only auto-enable Filter Active Semen when editing a saved record
    // with quantity ≤ 0. The inputValue check ensures we reset to default
    // filters after the semen has been cleared from the form.
    const shouldFilterActive =
      !!inputValue &&
      !!value?.id &&
      value?.quantity != null &&
      value.quantity <= 0;
    setFilterActiveSemen(shouldFilterActive);
    setFilterByRanch(shouldFilterActive ? false : !!ownerId);
    setFilterBySite(shouldFilterActive ? false : !!fertSiteId);
    setFilterByExport(shouldFilterActive ? false : !!exportId);
    setDialogVisible(true);
  };

  const handleEditSemen = (event: { node: { data: Semen } }) => {
    const selectedNode = event.api.getSelectedNodes()[0];
    if (selectedNode && selectedNode.data) {
      setSelectedSemen(selectedNode.data);
      setEditSemenDialogVisible(true);
    }
  };

  const handleCreateSemen = async (data: SemenFormValues) => {
    try {
      const transformedInput = {
        ...data,
        ownerId: data.owner,
        storageLocationId: data.storageLocation,
        sireId: data.sire.id,
        sexed: data.sexed === 'Yes' ? true : false,
        sex: SEX[data.sex as keyof typeof SEX],
        cssCertified: data.cssCertified === true ? 100000001 : 100000000,
      };

      const { owner, storageLocation, sire, ...rest } = transformedInput;

      const res = await addSemen(rest);

      if (res.data?.createNewSemen2?.success === false) {
        dispatch(
          addToast({
            severity: 'warn',
            summary: t('common.error'),
            detail: res.data.createNewSemen2.message,
            life: ToastLife.MEDIUM,
          }),
        );
      } else {
        dispatch(
          addToast({
            severity: 'success',
            summary: t('common.success'),
            detail: t('semen.semenCreatedSuccessfully'),
            life: ToastLife.MEDIUM,
          }),
        );
      }

      gridRef?.current?.api.refreshServerSide();
      gridRef?.current?.api.redrawRows();

      setNewSemenDialogVisible(false);
    } catch (error) {
      console.error('Failed to create semen:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('semen.failedToCreateSemen'),
          life: ToastLife.MEDIUM,
        }),
      );
    }
  };

  const handleSemenEditSuccess = (data: any) => {
    gridRef?.current?.api.refreshServerSide();
    gridRef?.current?.api.redrawRows();

    if (selectedSemen && selectedSemen.id === data.semenId) {
      setSelectedSemen({ ...selectedSemen, ...data });
    }

    setEditSemenDialogVisible(false);
    setSelectedSemenForEdit(undefined);
  };

  return (
    <div
      className={`[ ${styles.advancedSemenLookup} ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      <div className="flex items-end gap-2">
        <div className="label-input-group flex-1">
          <label>{label}</label>
          <InputText
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleOpenDialog();
              }
            }}
            placeholder={t('semen.searchSemen')}
            tabIndex={tabIndex}
            className="h-auto"
            data-skip-enter-advance={isAdvancedSemenDataSourceV2Enabled}
            disabled={disabled}
          />
        </div>
        <div className="flex items-end justify-end gap-2">
          <Button
            icon="pi pi-search"
            onClick={handleOpenDialog}
            aria-label={t('semen.searchSemen')}
          />
          {showEditButton && (
            <Button
              icon="pi pi-pencil"
              disabled={selectedSemen === null}
              onClick={() => setEditSemenDialogVisible(true)}
              aria-label={t('common.edit')}
            />
          )}
          {showAddButton && (
            <Button
              icon="pi pi-plus"
              aria-label={t('common.new')}
              onClick={() => setNewSemenDialogVisible(true)}
              disabled={disabled}
            />
          )}
          {ownerId ? (
            <ToggleButton
              offLabel={t('common.all')}
              onLabel={t('common.all')}
              onIcon="pi pi-check"
              offIcon="pi pi-times"
              checked={searchAllSemen}
              onChange={(e) => setSearchAllSemen(e.value)}
            />
          ) : null}
        </div>
      </div>
      <NewSemenDialog
        visible={newSemenDialogVisible}
        onHide={() => setNewSemenDialogVisible(false)}
        onSave={handleCreateSemen}
        gridRef={gridRef}
        data-testid="new-semen-dialog"
      />
      <EditSemenDialog
        visible={editSemenDialogVisible}
        onHide={() => setEditSemenDialogVisible(false)}
        onSuccess={handleSemenEditSuccess}
        semen={selectedSemenForEdit}
      />
      <Dialog
        ref={dialogRef}
        className={styles.dialog}
        header={t('semen.advancedSemenLookup')}
        visible={dialogVisible}
        maximizable
        modal
        onHide={handleCloseDialog}
        footer={() => dialogFooterTemplate(selectedSemen === null)}
        onResize={handleDialogResize}
      >
        <Card className={styles.card}>
          <form
            className={`${styles.lookupForm} label-input-column`.trim()}
            onSubmit={handleSearch}
          >
            <div className={styles.searchAndFilterWrapper}>
              <div className="searches">
                <div className="label-input-group">
                  <label>{t('common.searchBy')}</label>
                  <Dropdown
                    value={selectedSearchBy}
                    options={SEARCH_BY_OPTIONS_WITH_ALL}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => {
                      setSelectedSearchBy(e.target.value);
                      setSearchTermTemp('');
                      setSearchTerm('');
                    }}
                  />
                </div>
                <div className="label-input-group">
                  <label>{t('common.search')}</label>
                  <div className={styles.searchContainer}>
                    {isAdvancedSemenDataSourceV2Enabled &&
                    searchDropdownOptions ? (
                      <Dropdown
                        value={searchTermTemp}
                        options={searchDropdownOptions}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => {
                          setSearchTermTemp(e.value);
                        }}
                        showClear
                        placeholder={t('common.selectOption')}
                        disabled={disabled}
                      />
                    ) : (
                      <InputText
                        value={searchTermTemp}
                        onChange={(e) => setSearchTermTemp(e.target.value)}
                        data-skip-enter-advance
                        disabled={disabled}
                      />
                    )}
                    <Button
                      icon="pi pi-search"
                      type="submit"
                      aria-label={t('common.search')}
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
              <div className="label-input-group">
                <label>{t('common.filterBy')}</label>
                <div className={styles.filterContainer}>
                  <div className={styles.checkboxRow}>
                    <Checkbox
                      checked={filterByRanch}
                      onChange={(e) => {
                        setFilterByRanch(e.checked);
                      }}
                      id="filter-by-ranch"
                    />
                    <label htmlFor="filter-by-ranch">
                      {t('farm.filterByCurrentRanch')}
                    </label>
                  </div>
                  <div className={styles.checkboxRow}>
                    <Checkbox
                      checked={filterBySite}
                      onChange={(e) => {
                        setFilterBySite(e.checked);
                      }}
                      id="filter-by-site"
                    />
                    <label htmlFor="filter-by-site">
                      {t('farm.filterByCurrentFertSite')}
                    </label>
                  </div>
                  <div className={styles.checkboxRow}>
                    <Checkbox
                      checked={filterByExport}
                      onChange={(e) => {
                        setFilterByExport(e.checked);
                      }}
                      id="filter-by-export"
                    />
                    <label htmlFor="filter-by-export">
                      {t('farm.filterByCurrentExportLabel')}
                    </label>
                  </div>
                  <div className={styles.checkboxRow}>
                    <Checkbox
                      checked={filterActiveSemen}
                      onChange={(e) => {
                        setFilterActiveSemen(e.checked);
                        if (e.checked) {
                          setFilterByRanch(true);
                          setFilterBySite(true);
                        }
                      }}
                      id="filter-active-semen"
                    />
                    <label htmlFor="filter-active-semen">
                      {t('semen.filterActiveSemen')}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Card>
        {filterActiveSemen && (
          <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
            <span>{t('donorScheduling.revisionMode')}</span>
          </div>
        )}
        <WithLocalStorage storageKey="semen-lookup">
          <AgGridReact
            gridOptions={defaultGridOptions}
            className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''}`}
            ref={gridRef}
            defaultColDef={memoizedDefaultColDef}
            columnDefs={semenColumnDefs}
            getRowId={getRowId<Semen>((params) => String(params.data.id))}
            rowSelection="single"
            rowModelType="serverSide"
            serverSideDatasource={dataSource ?? undefined}
            cacheBlockSize={CACHE_BLOCK_SIZE}
            blockLoadDebounceMillis={
              isAdvancedSemenDataSourceV2Enabled
                ? LOAD_DEBOUNCE_MS_V2
                : LOAD_DEBOUNCE_MS
            }
            pagination={true}
            paginationAutoPageSize={true}
            paginationPageSize={DATA_TABLE_ROWS}
            onSelectionChanged={handleSelectionChanged}
            onCellDoubleClicked={handleEditSemen}
            sideBar={
              isSpecificViewContextEnabled
                ? lookupSideBar('advanced-semen-lookup')
                : sideBar
            }
          />
        </WithLocalStorage>
      </Dialog>
      <ConfirmDialog
        visible={showShipmentConfirmDialog}
        onHide={() => {
          setShowShipmentConfirmDialog(false);
          setPendingSemenSelection(null);
        }}
        message={t('shipping.semenAttachedToShipmentWarning')}
        header={t('shipping.shipmentWarning')}
        icon="pi pi-exclamation-triangle"
        accept={() => {
          if (pendingSemenSelection) {
            proceedWithSelection(pendingSemenSelection);
          }
          setShowShipmentConfirmDialog(false);
        }}
        reject={() => {
          setShowShipmentConfirmDialog(false);
          setPendingSemenSelection(null);
        }}
        acceptLabel={t('common.yes')}
        rejectLabel={t('common.no')}
      />
    </div>
  );
};
